/**
 * Full repo-news view: the newest digest expanded (overall line + one
 * paragraph per domain), older digests collapsible underneath — the history
 * so a digest is never gone for good.
 */

import type { NewsEntry } from "../lib/news";
import { renderMarkdown } from "../lib/markdown";

function EntryBody({ entry }: { entry: NewsEntry }) {
  return (
    <>
      {entry.overall && (
        <p className="mb-3 text-sm leading-relaxed text-ink-200">{entry.overall}</p>
      )}
      {entry.domains.map((d) => (
        <section key={d.domain} className="mb-4 last:mb-0">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gold">
            {d.domain}
          </h3>
          <div
            className="prose-item text-sm"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(d.body) }}
          />
        </section>
      ))}
    </>
  );
}

export function NewsDetail({ entries, onBack }: { entries: NewsEntry[]; onBack: () => void }) {
  const newest = entries.length > 0 ? entries[entries.length - 1] : null;
  const older = entries.slice(0, -1).reverse();
  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-28 pt-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button onClick={onBack} className="text-sm text-ink-400 hover:text-ink-200">
          ‹ Back
        </button>
        <span className="chip bg-gold/15 text-gold">📰 Repo news</span>
      </div>

      {!newest ? (
        <div className="card p-6 text-center text-sm text-ink-400">
          No repo news yet — the next nightly run writes the first digest.
        </div>
      ) : (
        <>
          <div className="mb-2 text-xs text-ink-500">
            {newest.generatedAt.replace("T", " ")}
            {newest.agents ? ` · ${newest.agents}` : ""}
          </div>
          <div className="card mb-6 p-4">
            <EntryBody entry={newest} />
          </div>

          {older.length > 0 && (
            <details className="mb-4">
              <summary className="cursor-pointer text-xs text-ink-500">
                Earlier updates ({older.length})
              </summary>
              <div className="mt-3 space-y-3">
                {older.map((e, i) => (
                  <details key={`${e.generatedAt}-${i}`} className="card p-3">
                    <summary className="cursor-pointer text-xs text-ink-400">
                      {e.generatedAt.replace("T", " ")}
                      {e.overall ? ` · ${e.overall.slice(0, 90)}` : ""}
                    </summary>
                    <div className="mt-3">
                      <EntryBody entry={e} />
                    </div>
                  </details>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
